import React, { Fragment } from 'react';

import {
  Dropdown,
  Field,
  Grid,
  Margin,
  Spacer,
  Text,
  TextInput,
} from '@zapier/design-system';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';
import { BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import {
  PUBLISHING_FORM_FIELD_BLANK_VALUES as FIELD_BLANK_VALUES,
  useForm,
} from 'app/developer-v3/hooks';

import Step from './Step';
import { PAID_API_OPTIONS } from './constants';
import { ReadinessValues as Values } from './types';
import { useStepCompletion } from './useStepCompletion';
import { getError, parseSaveErrors } from './utils';

type Props = {
  initialValues: Values;
  isDisabled: boolean;
  onComplete: (isComplete: boolean) => void;
  onSubmit: (valuesNext: Values) => Promise<void>;
};

export default function Readiness({
  initialValues,
  isDisabled,
  onComplete,
  onSubmit,
}: Props) {
  const form = useForm<Values>({
    enableReinitialize: true,
    initialValues,
    onSubmit: async formValues => {
      try {
        await onSubmit(formValues);
      } catch (errors) {
        Object.entries(parseSaveErrors(errors)).forEach(([key, value]) => {
          form.setFieldError(key, value as string);
        });
      }
    },
    validate: (values: Values) => {
      const errors: Partial<Record<keyof Values, string>> = {};
      if (values.hasApiOwnership === FIELD_BLANK_VALUES.hasApiOwnership) {
        errors.hasApiOwnership = 'Required.';
      }
      if (values.hasApiOwnership === false) {
        if (values.apiEndpointsUsed === FIELD_BLANK_VALUES.apiEndpointsUsed) {
          errors.apiEndpointsUsed = 'Required.';
        }
        if (values.ownsAllEndpoints === FIELD_BLANK_VALUES.ownsAllEndpoints) {
          errors.ownsAllEndpoints = 'Required.';
        }
        if (
          values.ownsAllEndpoints === false &&
          values.hasPermissionForEndpoints ===
            FIELD_BLANK_VALUES.hasPermissionForEndpoints
        ) {
          errors.hasPermissionForEndpoints = 'Required.';
        }
      }
      if (
        values.usesProductionEndpoints ===
        FIELD_BLANK_VALUES.usesProductionEndpoints
      ) {
        errors.usesProductionEndpoints = 'Required.';
      }
      // paidApi is a dropdown in the UI with `undefined` to be the default
      // value, making no option selected initially. It is stored as a string
      // with a default value of `''`. If the form was saved with no paidApi
      // option selected, paidApi is assigned to the empty string value. In both
      // cases, no option is selected, so we check for `undefined` and `''`
      // simultaneously.
      //
      // Notice that other dropdowns in the form are a yes or no question and
      // stored as a boolean with a default value of `undefined`. Their values
      // are `undefined` whether the form was saved or not, so we only check
      // `undefined` for those.
      if ([FIELD_BLANK_VALUES.paidApi, ''].includes(values.paidApi)) {
        errors.paidApi = 'Required.';
      }
      return errors;
    },
    validateOnMount: true,
  });

  const isTouched = Object.values(form.isTouched).some(value => value);
  const isCompleted = !form.isDirty && form.isValid;
  useStepCompletion(isCompleted, onComplete);

  return (
    <Step
      isComplete={!form.isDirty && form.isValid}
      isError={isTouched && !form.isValid}
      title="Integration readiness & API ownership"
    >
      <Grid columns="1" gap="20px">
        <Field
          error={getError('hasApiOwnership', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Are you using any APIs hosted on a domain you do not own?"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              Select "Yes" if an API endpoint is not clearly owned and operated
              by your company—for example, if the base URL does not include your
              company’s domain name. Review this{' '}
              <Link
                href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#3-1-apis`}
                target="_blank"
              >
                publishing requirement
              </Link>
              .
            </Text>
          )}
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              aria-label="Are you using any APIs hosted on a domain you do not own?"
              isErrored={
                form.isTouched.hasApiOwnership && !!form.errors.hasApiOwnership
              }
              items={[
                // Notice that we are using the opposite values here. Yes means
                // the user is using APIs hosted on a domain they do not own,
                // so hasApiOwnership is false. No means the user is not using
                // APIs hosted on a domain they do not own, so hasApiOwnership
                // is true.
                { label: 'Yes', value: false },
                { label: 'No', value: true },
              ]}
              menuAriaLabel="Yes or no options"
              onBlur={() => form.setFieldTouched('hasApiOwnership', true)}
              onSelect={({ value }) =>
                form.setFieldValue('hasApiOwnership', value)
              }
              selectedItem={{
                label:
                  form.values.hasApiOwnership === true
                    ? 'No'
                    : form.values.hasApiOwnership === false
                    ? 'Yes'
                    : '',
                value: form.values.hasApiOwnership,
              }}
            />
          )}
        />
        {form.values.hasApiOwnership === false && (
          <Margin margin="0px 0px 0px 20px">
            <Grid columns="1" gap="20px">
              <Field
                error={getError(
                  'apiEndpointsUsed',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Which API endpoints are you using?"
                renderHelpText={() => (
                  <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
                    Type the base API endpoint(s) you're using, e.g.
                    api.yourcompany.com
                  </Text>
                )}
                renderInput={inputProps => (
                  <TextInput
                    {...inputProps}
                    aria-label="Which API endpoints are you using?"
                    name="apiEndpointsUsed"
                    onBlur={form.onBlur}
                    onChange={form.onChange}
                    value={form.values.apiEndpointsUsed}
                  />
                )}
              />
              <Field
                error={getError(
                  'ownsAllEndpoints',
                  form.isTouched,
                  form.errors
                )}
                isDisabled={isDisabled}
                isRequired={true}
                label="Do you own and operate all of these endpoints?"
                renderInput={inputProps => (
                  <Dropdown
                    {...inputProps}
                    aria-label="Do you own and operate all of these endpoints?"
                    isErrored={
                      form.isTouched.ownsAllEndpoints &&
                      !!form.errors.ownsAllEndpoints
                    }
                    items={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                    menuAriaLabel="Yes or no options"
                    onBlur={() =>
                      form.setFieldTouched('ownsAllEndpoints', true)
                    }
                    onSelect={({ value }) =>
                      form.setFieldValue('ownsAllEndpoints', value)
                    }
                    selectedItem={{
                      label:
                        form.values.ownsAllEndpoints === true
                          ? 'Yes'
                          : form.values.ownsAllEndpoints === false
                          ? 'No'
                          : '',
                      value: form.values.ownsAllEndpoints,
                    }}
                  />
                )}
              />
              {form.values.ownsAllEndpoints === false && (
                <Margin margin="0px 0px 0px 20px">
                  <Field
                    error={getError(
                      'hasPermissionForEndpoints',
                      form.isTouched,
                      form.errors
                    )}
                    isDisabled={isDisabled}
                    isRequired={true}
                    label="Confirm that you have permission from the owners of these endpoints to use them."
                    renderHelpText={() => (
                      <Fragment>
                        <Text
                          color={isDisabled ? 'GrayWarm5' : undefined}
                          fontWeight={700}
                          tag="p"
                        >
                          Be sure to state the company that owns the endpoints
                        </Text>
                        <Text
                          color={isDisabled ? 'GrayWarm5' : undefined}
                          tag="p"
                        >
                          , and that you have permission from that company to
                          use these endpoints in your Zapier integration. We
                          need written confirmation from you that you have
                          permission.
                        </Text>
                      </Fragment>
                    )}
                    renderInput={inputProps => (
                      <TextInput
                        {...inputProps}
                        aria-label="Confirm that you have permission from the owners of these endpoints to use them."
                        name="hasPermissionForEndpoints"
                        onBlur={form.onBlur}
                        onChange={form.onChange}
                        value={form.values.hasPermissionForEndpoints}
                      />
                    )}
                  />
                </Margin>
              )}
            </Grid>
          </Margin>
        )}
        <Field
          error={getError(
            'usesProductionEndpoints',
            form.isTouched,
            form.errors
          )}
          isDisabled={isDisabled}
          isRequired={true}
          label="Does the integration use production API endpoints?"
          renderHelpText={() => (
            <Text color={isDisabled ? 'GrayWarm5' : undefined} tag="p">
              We need confirmation that the integration uses production API
              endpoints and meets this{' '}
              <Link
                href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements#5-4-integration-uses-production-apis`}
                target="_blank"
              >
                publishing requirement
              </Link>
              . Please double-check all API endpoints used in the integration
              (Authentication, Triggers, Actions, etc) and ensure they’re set to
              production endpoints.
            </Text>
          )}
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              aria-label="Does the integration use production API endpoints?"
              isErrored={
                form.isTouched.usesProductionEndpoints &&
                !!form.errors.usesProductionEndpoints
              }
              items={[
                { label: 'Yes', value: true },
                { label: 'No', value: false },
              ]}
              menuAriaLabel="Yes or no options"
              onBlur={() =>
                form.setFieldTouched('usesProductionEndpoints', true)
              }
              onSelect={({ value }) =>
                form.setFieldValue('usesProductionEndpoints', value)
              }
              selectedItem={{
                label:
                  form.values.usesProductionEndpoints === true
                    ? 'Yes'
                    : form.values.usesProductionEndpoints === false
                    ? 'No'
                    : '',
                value: form.values.usesProductionEndpoints,
              }}
            />
          )}
        />
        <Field
          error={getError('paidApi', form.isTouched, form.errors)}
          isDisabled={isDisabled}
          isRequired={true}
          label="Do your users need to pay anything additional in order to use this
              Zapier/API app?"
          renderInput={inputProps => (
            <Dropdown
              {...inputProps}
              aria-label="Do your users need to pay anything additional in order to use this Zapier/API app?"
              isErrored={form.isTouched.paidApi && !!form.errors.paidApi}
              items={Object.values(PAID_API_OPTIONS)}
              menuAriaLabel="List of paid API options"
              onBlur={() => form.setFieldTouched('paidApi', true)}
              onSelect={({ value: nextPaidApi }) =>
                form.setFieldValue('paidApi', nextPaidApi)
              }
              selectedItem={PAID_API_OPTIONS[form.values.paidApi]}
            />
          )}
        />
      </Grid>
      <Spacer height="20px" />
      <AsyncButton
        aria-label="Save integration readiness"
        color="secondary"
        disabled={
          isDisabled || !form.isDirty || !form.isValid || form.isSubmitting
        }
        isWorking={form.isSubmitting}
        onClick={() => form.onSubmit()}
        type="button"
      >
        Save
      </AsyncButton>
    </Step>
  );
}
